import { Component, ElementRef, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { filter, map, take, withLatestFrom, startWith } from 'rxjs/operators';
import { forkJoin, Observable, combineLatest, Subscription } from 'rxjs';
import _ from 'lodash';
import { IDsBreadcrumbItem } from '@levelaccess/design-system';
import {
  $artificialIntelligence,
  $privacySettings,
  $tenant,
  $tenantMetadata,
  $tenants,
  $tenantUser,
  $tenantUserIntegrations,
  DEFAULT_DP_COMPLEXITY_COUNTS,
} from '@shared/constants/tenant';
import { $academy } from '@shared/constants/academy';
import { Api } from '@shared/constants/api';
import { IScannableDocumentPdfPagesTestStateResponse } from '@shared/interfaces/scannable-document.interface';
import { ISkilljarSignupField, ISkilljarSignupFieldOption } from '@shared/interfaces/academy-integration.interface';
import { errorMessagesNames } from '@shared/constants/errors';
import {
  IEditTenantRequest,
  ITenant,
  ITenantDigitalPropertyLimitState,
  ITenantArtificialIntelligence,
  ITenantDpComplexityCounts,
  ITenantsResponse,
  ITenantUser,
  ITenantWithPackageId,
} from '@shared/interfaces/tenant.interface';
import { IPackage, IPackageAddon } from '@shared/interfaces/packaging.interface';
import { $package, $packageAddon, FunctionalAreaSetting, PackageAddonNames, PackageNames } from '@shared/constants/packaging';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { $salesforceAccount } from '@shared/constants/salesforce';
import { ISalesforceAccount } from '@shared/interfaces/salesforce.interface';
import { DsModalService, DsModalVariant } from '@levelaccess/design-system';
import { RoutingService } from '../../../services/routing.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { ErrorMessageService } from '../../../services/error-message.service';
import { TenantService } from '../../../services/tenant.service';
import { AppConfigService } from '../../../services/app-config.service';
import { TranslateService } from '../../../translate/translate.service';
import { A11yService } from '../../../services/a11y.service';
import { InputFieldContext } from '../../../components/common-input/common-input.component';
import { DateUtility } from '../../../utility/date.utility';
import { CommonUtility } from '../../../utility/common.utility';
import {
  AbstractTenantComponent,
  Alerts,
  ArtificialIntelligenceOption,
  FormFields,
} from '../upsert-tenant/abstract-tenant.component';
import { PackageService } from '../../../services/package.service';
import { NotificationService } from '../../../services/notification.service';
import { AngularUtility } from '../../../utility/angular.utility';
import { PromptConfirmationModalOptions, PromptDialogListItem } from '../../../interfaces/modal-dialog.interface';
import { PromptConfirmationModalComponent } from '../../../components/modals/prompt-confirmation-modal/prompt-confirmation-modal.component';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { UsageLimitFieldContext } from '../upsert-tenant/usage-limit-component/usage-limit.component';

interface MajorTenantValues {
  [FormFields.name]?: string;
  [FormFields.package]: IPackage;
  [FormFields.salesforceAccount]?: ISalesforceAccount;
  [FormFields.pdfPagesTestLimit]: number;
  [FormFields.digitalPropertyLimit]: number;
  [FormFields.monitoringPagesPerDigitalPropertyLimit]: number;
  [FormFields.seatLimit]: number;
  [FormFields.userWayWebsiteLimit]?: number;
  [FormFields.externalPackageSignupField]?: ISkilljarSignupFieldOption;
  [FormFields.artificialIntelligence]: string[];
  [FormFields.dpComplexityCounts]?: ITenantDpComplexityCounts;
}

@Component({
  selector: 'app-edit-tenant',
  templateUrl: '../upsert-tenant/upsert-tenant.component.html',
  styleUrls: ['../upsert-tenant/upsert-tenant.component.scss'],
  standalone: false,
})
export class EditTenantComponent extends AbstractTenantComponent implements OnInit {
  public doneLoading: boolean = false;
  public errorMessage: string = 'edit_tenant_error';
  public breadcrumbs: IDsBreadcrumbItem[];

  private originalValuesSnapshot: MajorTenantValues;
  private _displayPackages$: Observable<IPackage[]>;
  get displayPackages$(): Observable<IPackage[]> {
    return this._displayPackages$;
  }

  protected alertClosed: Record<Alerts, boolean> = {
    [Alerts.name]: true,
    [Alerts.salesforceAccount]: true,
    [Alerts.subdomain]: true,
  };

  public constructor(
    routingService: RoutingService,
    formBuilder: UntypedFormBuilder,
    errorMessageService: ErrorMessageService,
    tenantService: TenantService,
    appConfigService: AppConfigService,
    translateService: TranslateService,
    a11yService: A11yService,
    notificationService: NotificationService,
    packageService: PackageService,
    featureFlagService: FeatureFlagService,
    router: Router,
    private dsModalService: DsModalService,
    private activatedRoute: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private element: ElementRef<Element>,
  ) {
    super(
      routingService,
      formBuilder,
      appConfigService,
      errorMessageService,
      notificationService,
      tenantService,
      translateService,
      a11yService,
      packageService,
      featureFlagService,
      router,
    );
    this.tenantId = this.activatedRoute.snapshot.params[Api.tenantId];
  }

  private convertTenantArtificialIntelligence(aiData: ITenantArtificialIntelligence | undefined): string[] {
    const tenantDidNotOptOut: boolean = aiData?.[$artificialIntelligence.enabled] ?? true;

    return Object.values(ArtificialIntelligenceOption).filter((field: $artificialIntelligence): boolean => {
      const isEnabledField: boolean = field === $artificialIntelligence.enabled;
      return isEnabledField && tenantDidNotOptOut;
    });
  }

  private onGetTenantSuccess(tenant: ITenantWithPackageId, packages: IPackage[], fields?: ISkilljarSignupField): void {
    const packageAtHand: IPackage = packages.find((pack: IPackage) => pack[$package._id] === tenant.packageId);
    if (SharedCommonUtility.isNullish(packageAtHand)) {
      this.errorHandlerService.handleError(new Error(`Cannot find tenant's package for id: ${tenant.packageId}`));
    }

    this.form.get(FormFields.package).setValue(packageAtHand);

    let skilljarGroupSignupField: ISkilljarSignupFieldOption;
    if (SharedCommonUtility.notNullish(fields)) {
      skilljarGroupSignupField = fields.options.find(
        (field: ISkilljarSignupFieldOption) => field.value === tenant.academyConfig?.externalPackageSignupField,
      );
      this.form.get(FormFields.externalPackageSignupField).setValue(skilljarGroupSignupField);
    }

    if (SharedCommonUtility.notNullish(tenant[$tenant.salesforceAccountId])) {
      this.selectedSalesforceAccount = {
        [$salesforceAccount.id]: tenant[$tenant.salesforceAccountId],
        [$salesforceAccount.name]: tenant[$tenant.salesforceAccountName],
      };
    }

    if (this.showSalesforceField) {
      this.form.get(FormFields.salesforceAccount).setValue(this.selectedSalesforceAccount);
    }

    this.form.get(FormFields.name).setValue(tenant[$tenant.name]);
    this.form.get(FormFields.internalNotes).setValue(tenant[$tenant.internalNotes]);
    this.form.get(FormFields.description).setValue(tenant[$tenant.description]);

    const aiValue: string[] = this.convertTenantArtificialIntelligence(tenant[$tenant.artificialIntelligence]);
    this.form.get(FormFields.artificialIntelligence).setValue(aiValue);

    if (this.featureFlagService.variation(this.FeatureFlagCollection.digitalAssetsPrivacySetting, false)) {
      const privacySettingsFormGroup = this.form.get(FormFields.privacySettings) as UntypedFormGroup;
      privacySettingsFormGroup.patchValue(
        {
          privacyObfuscationEnabled: tenant[$tenant.privacySettings]?.[$privacySettings.privacyObfuscationEnabled] ?? false,
        },
        { emitEvent: false },
      );
    }

    if (this.canToggleScanningAgentDebugMode) {
      this.form.get(FormFields.scanningAgentDebugMode).patchValue(tenant[$tenant.scanningAgentDebugMode] ?? false, {
        emitEvent: false,
      });
    }

    this.form.get(FormFields.scanDefaultTimeoutHours).setValue(tenant[$tenant.scanDefaultTimeoutHours] ?? null, {
      emitEvent: false,
    });

    if (
      !this.featureFlagService.variation(this.FeatureFlagCollection.auditOnlyPackage, false) &&
      packageAtHand[$package.name] === PackageNames.Audit
    ) {
      this.isPackageSelectionDisabled = true;
    }

    this.calculateUsageLimitValues(tenant);
    this.updateUsageLimitValidators(packageAtHand);

    this.originalValuesSnapshot = {
      [FormFields.name]: tenant[$tenant.name],
      ...(this.showSalesforceField ? { [FormFields.salesforceAccount]: this.selectedSalesforceAccount } : {}),
      [FormFields.package]: packageAtHand,
      [FormFields.pdfPagesTestLimit]: _.max([tenant[$tenant.pdfPagesTestLimit], 0]),
      [FormFields.digitalPropertyLimit]: _.max([tenant[$tenant.digitalPropertyLimit], 0]),
      [FormFields.monitoringPagesPerDigitalPropertyLimit]: _.max([tenant[$tenant.monitoringPagesPerDigitalPropertyLimit], 0]),
      [FormFields.seatLimit]: _.max([tenant[$tenant.academyConfig][$academy.seatLimit], 0]),
      [FormFields.userWayWebsiteLimit]: tenant[$tenant.userWayWebsiteLimit] ?? 0,
      [FormFields.artificialIntelligence]: aiValue,
      [FormFields.dpComplexityCounts]: this.normalizeComplexityCounts(tenant[$tenant.dpComplexityCounts]),
    };

    if (SharedCommonUtility.notNullish(skilljarGroupSignupField)) {
      this.originalValuesSnapshot[FormFields.externalPackageSignupField] = skilljarGroupSignupField;
    }

    this.personalizedSubdomain = `https://${tenant[$tenant.domain]}.${this.tenantBaseUrl}`;
    this.tenantCreatedAt = DateUtility.getFormattedISODate(tenant[$tenant.createdAt] as string, false);
  }

  private calculateUsageLimitValues(tenant: ITenantWithPackageId): void {
    this.allSelectedAddonIds = tenant.packageAddonIds ?? [];

    this.form.get(FormFields.pdfPagesTestLimit).setValue(_.max([tenant[$tenant.pdfPagesTestLimit], 0]));

    this.form
      .get(FormFields.monitoringPagesPerDigitalPropertyLimit)
      .setValue(_.max([tenant[$tenant.monitoringPagesPerDigitalPropertyLimit], 0]));

    this.form.get(FormFields.seatLimit).setValue(_.max([tenant[$tenant.academyConfig][$academy.seatLimit], 0]));

    this.form.get(FormFields.digitalPropertyLimit).setValue(_.max([tenant[$tenant.digitalPropertyLimit], 0]));

    const dpComplexityCountsControl = this.form.get(FormFields.dpComplexityCounts);
    if (dpComplexityCountsControl) {
      const normalizedCounts = this.normalizeComplexityCounts(tenant[$tenant.dpComplexityCounts]);
      dpComplexityCountsControl.setValue(normalizedCounts, { emitEvent: false });
    }

    const currentUserWayFormLimit: number = this.form.get(FormFields.userWayWebsiteLimit)?.value ?? 0;
    if (currentUserWayFormLimit === 0) {
      const newValue: number = _.max([tenant[$tenant.userWayWebsiteLimit], 0]);
      this.form.get(FormFields.userWayWebsiteLimit).setValue(newValue);
    }
  }

  private onGetTenantFailure(response: HttpErrorResponse): void {
    this.handleError('get_tenant_error', response);
  }

  private getSelectedTenant(): void {
    this.tenant$ = this.tenantService.getTenant(this.tenantId).pipe(AngularUtility.shareRef());
    this.subscription.add(
      forkJoin([this.tenant$, this.packages$, this.availableSignupFields$]).subscribe({
        next: ([tenant, packageForm, fields]: [ITenantWithPackageId, IPackage[], ISkilljarSignupField]): void =>
          this.onGetTenantSuccess(tenant, packageForm, fields),
        error: this.onGetTenantFailure.bind(this),
        complete: () => {
          this.doneLoading = true;
        },
      }),
    );
  }

  private navigateToAdminTenantsPage(): void {
    this.router
      .navigate([this.tenantRoutePath])
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private onEditTenantError(response: HttpErrorResponse): void {
    if (CommonUtility.extractHTTPErrorName(response) === errorMessagesNames.AcademyUpdateFailed) {
      const messageKey: string = this.errorMessageService.getAppErrorResponse(response);
      this.handleError(messageKey, response);
    }

    this.handleError('error_edit_tenant', response);
  }

  public getPdfPagesTestInputContext(): UsageLimitFieldContext {
    return {
      label: 'label_pdf_pages',
      field: FormFields.pdfPagesTestLimit,
      tooltip: 'total_pdf_tests_tooltip',
      overrideErrors: {
        min: this.translateService.instant('form_error_min_total_pdf_test'),
      },
      maxLimit: this.maxPdfPagesTestLimit,
    };
  }

  private getOriginalIncludedValue(formField: FormFields): number {
    const value = this.originalValuesSnapshot[formField];
    return isNaN(Number(value)) ? 0 : Math.max(Number(value), 0);
  }

  private getNewIncludedValue(formField: FormFields): number {
    const control = this.form.get(formField);
    if (!control) {
      return 0;
    }
    const value = control.value;
    if (SharedCommonUtility.isNullish(value) || value === '') {
      return 0;
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
    return isNaN(numValue) ? 0 : Math.max(numValue, 0);
  }

  private normalizeComplexityCounts(counts: ITenantDpComplexityCounts | null | undefined): ITenantDpComplexityCounts {
    if (!this.showDpComplexityCalculator) {
      return null;
    }

    if (!counts) {
      return { ...DEFAULT_DP_COMPLEXITY_COUNTS };
    }

    return {
      high: _.max([counts.high, 0]),
      medium: _.max([counts.medium, 0]),
      low: _.max([counts.low, 0]),
    };
  }

  public getListTenantPackageChanges(): PromptDialogListItem[] {
    const changes: PromptDialogListItem[] = [];

    const originalName: string = this.originalValuesSnapshot[FormFields.name];
    const newName: string = this.form.get(FormFields.name).value;
    if (originalName !== newName) {
      changes.push({
        messageKey: 'label_changed_tenant_name',
        arguments: [
          { value: originalName, highlight: true },
          { value: newName, highlight: true },
        ],
      });
    }

    if (this.showSalesforceField) {
      const originalSalesforceAccount: ISalesforceAccount = this.originalValuesSnapshot[FormFields.salesforceAccount];

      if (originalSalesforceAccount?.[$salesforceAccount.id] !== this.selectedSalesforceAccount?.[$salesforceAccount.id]) {
        changes.push(this.getSalesforceAccountChange(this.selectedSalesforceAccount));
      }
    }

    const originalSignupField: ISkilljarSignupFieldOption = this.originalValuesSnapshot[FormFields.externalPackageSignupField];
    const newSignupField: ISkilljarSignupFieldOption = this.form.get(FormFields.externalPackageSignupField)?.value;
    if (originalSignupField?.id !== newSignupField?.id) {
      changes.push({
        messageKey: 'label_changed_tenant_academy_package',
        arguments: [
          { value: originalSignupField?.value ?? 'none', highlight: true },
          { value: newSignupField.value, highlight: true },
        ],
      });
    }

    const originalPackage: IPackage = this.originalValuesSnapshot[FormFields.package];
    const newPackage: IPackage = this.form.get(FormFields.package).value;
    if (originalPackage?.[$package._id] !== newPackage?.[$package._id]) {
      changes.push({
        messageKey: 'label_changed_tenant_package',
        arguments: [
          { value: originalPackage?.[$package.name] ?? 'none', highlight: true },
          { value: newPackage[$package.name] ?? 'none', highlight: true },
        ],
      });
    }

    if (this.artificialIntelligenceEnabled) {
      const originalAI: string[] = this.originalValuesSnapshot[FormFields.artificialIntelligence];
      const newAI: string[] = this.form.get(FormFields.artificialIntelligence)?.value ?? [];

      if (JSON.stringify(originalAI.sort()) !== JSON.stringify(newAI.sort())) {
        type AIStatus = 'enabled' | 'disabled';
        const originalStatus: AIStatus = originalAI.includes(ArtificialIntelligenceOption.enable_ai_feature)
          ? 'enabled'
          : 'disabled';
        const newStatus: AIStatus = newAI.includes(ArtificialIntelligenceOption.enable_ai_feature) ? 'enabled' : 'disabled';

        if (originalStatus !== newStatus) {
          changes.push({
            messageKey: 'label_changed_tenant_artificial_intelligence',
            arguments: [
              { value: originalStatus, highlight: true },
              { value: newStatus, highlight: true },
            ],
          });
        }
      }
    }

    const usageLimitChange = (formField: FormFields, messageKey: string): void => {
      const originalValue: number = this.getOriginalIncludedValue(formField);
      const newValue: number = this.getNewIncludedValue(formField);
      if (originalValue !== newValue) {
        changes.push({
          messageKey,
          arguments: [
            { value: originalValue, highlight: false },
            { value: newValue, highlight: false },
          ],
        });
      }
    };

    usageLimitChange(FormFields.seatLimit, 'label_changed_tenant_academy_seat_limit');
    usageLimitChange(FormFields.digitalPropertyLimit, 'label_changed_tenant_DP_limit');
    usageLimitChange(FormFields.monitoringPagesPerDigitalPropertyLimit, 'label_changed_tenant_monitoring_limit');
    usageLimitChange(FormFields.pdfPagesTestLimit, 'label_changed_tenant_PDF_limit');
    if (this.showUserWayWebsiteLimit) {
      usageLimitChange(FormFields.userWayWebsiteLimit, 'label_changed_tenant_widget_website_limit');
    }

    return changes;
  }

  private getSalesforceAccountChange(account: ISalesforceAccount): PromptDialogListItem {
    if (SharedCommonUtility.isNullish(account)) {
      return {
        messageKey: `label_unlinked_tenant_salesforce`,
        arguments: [],
      };
    }

    return {
      messageKey: `label_changed_tenant_salesforce`,
      arguments: [{ value: `${account[$salesforceAccount.name]} (SFID: ${account[$salesforceAccount.id]})`, highlight: true }],
    };
  }

  public get needsConfirmationPrompt(): boolean {
    return this.getListTenantPackageChanges().length > 0;
  }

  public async userConfirmedPrompt(): Promise<boolean> {
    const modalRef = this.dsModalService.open(PromptConfirmationModalComponent, DsModalVariant.center);
    const modal: PromptConfirmationModalComponent = modalRef.componentInstance as PromptConfirmationModalComponent;
    const modalOptions: PromptConfirmationModalOptions = {
      headerKey: 'label_confirm_major_tenant_changes_header',
      descriptionKey: 'label_confirm_major_tenant_changes_description',
      listHeaderKey: 'label_confirm_major_tenant_changes_list_header',
      listItems: this.getListTenantPackageChanges(),
      promptConfirmKey: 'label_type_text_below_to_confirm_changes',
      promptInputKey: 'label_confirm_major_tenant_input',
      confirmButtonKey: 'confirm_changes',
    };
    modal.init(modalOptions);

    return new Promise<boolean>((resolve: (value: boolean) => void) => {
      const subscription: Subscription = new Subscription();
      subscription.add(
        modal.confirm.pipe(take(1)).subscribe((): void => {
          subscription.unsubscribe();
          resolve(true);
        }),
      );
      subscription.add(
        modal.cancelled.pipe(take(1)).subscribe((): void => {
          subscription.unsubscribe();
          resolve(false);
        }),
      );
    });
  }

  public onClose(): void {
    this.navigateToAdminTenantsPage();
  }

  public async submit(): Promise<void> {
    if (this.form.pristine) {
      this.navigateToAdminTenantsPage();
      return;
    }

    this.errorResponseMessagesSet.clear();
    this.formValidationRequest.next();

    if (this.form.valid === false) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    if (this.needsConfirmationPrompt) {
      if (!(await this.userConfirmedPrompt())) {
        return;
      }
    }
    const showInternalNotes: boolean = this.featureFlagService.variation(
      this.FeatureFlagCollection.showInternalNotesHideDescription,
      false,
    );

    const editRequest: IEditTenantRequest = {
      ...this.formTenantNameAndSalesforceAccount,
      ...(showInternalNotes
        ? { [$tenant.internalNotes]: this.form.get(FormFields.internalNotes).value?.trim() }
        : { [$tenant.description]: this.form.get(FormFields.description).value?.trim() }),
      [$tenant.pdfPagesTestLimit]: Number(this.form.get(FormFields.pdfPagesTestLimit).value),
      [$tenant.monitoringPagesPerDigitalPropertyLimit]: Number(
        this.form.get(FormFields.monitoringPagesPerDigitalPropertyLimit).value,
      ),
      [$academy.seatLimit]: Number(this.form.get(FormFields.seatLimit).value),
      [$tenantMetadata.packageId]: this.form.get(FormFields.package).value[$package._id],
      [$tenant.digitalPropertyLimit]: Number(this.form.get(FormFields.digitalPropertyLimit).value),
      ...(this.hasPackageAddons && {
        [$tenantMetadata.packageAddonIds]: this.getFilteredPackageAddons(),
      }),
      ...this.formTenantArtificialIntelligence,
      ...this.formTenantPrivacySettings,
      ...this.formTenantScanningAgentDebugMode,
      [$tenant.scanOptions]: this.form.get(FormFields.scanOptions)?.value,
    };

    const externalPackageSignupFieldControl = this.form.get(FormFields.externalPackageSignupField);
    if (SharedCommonUtility.notNullish(externalPackageSignupFieldControl?.value)) {
      editRequest[$academy.externalPackageSignupField] = externalPackageSignupFieldControl?.value?.value;
    }

    editRequest[$tenant.dpComplexityCounts] = this.normalizeComplexityCounts(this.form.get(FormFields.dpComplexityCounts)?.value);

    const scanDefaultTimeoutHoursValue: number | null = this.form.get(FormFields.scanDefaultTimeoutHours)?.value;
    editRequest[$tenant.scanDefaultTimeoutHours] = SharedCommonUtility.notNullish(scanDefaultTimeoutHoursValue)
      ? Number(scanDefaultTimeoutHoursValue)
      : null;

    if (this.showUserWayWebsiteLimit) {
      editRequest[$tenant.userWayWebsiteLimit] = Number(this.form.get(FormFields.userWayWebsiteLimit).value);
    }

    this.subscription.add(
      this.tenantService.updateTenant(this.tenantId, editRequest).subscribe({
        next: this.navigateToAdminTenantsPage.bind(this),
        error: this.onEditTenantError.bind(this),
      }),
    );
  }

  private updateUsageLimitIfValidPackage(): void {
    const packageValue: IPackage = this.form.get(FormFields.package)?.value;
    if (SharedCommonUtility.notNullish(packageValue)) {
      this.updateUsageLimitValidators(packageValue);
    }
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.getSelectedTenant();
    this._displayPackages$ = combineLatest([
      this.packages$,
      this.tenant$,
      this.featureFlagService.variation$(this.FeatureFlagCollection.createLegacyEA, false),
      this.featureFlagService.variation$(this.FeatureFlagCollection.auditOnlyPackage, false),
    ]).pipe(
      map(
        ([packages, tenant, createLegacyEAFlag, auditOnlyPackageFlag]: [
          IPackage[],
          ITenantWithPackageId,
          boolean,
          boolean,
        ]): IPackage[] => {
          const packageNamesToRemove: string[] = [];
          const legacyEApackage: IPackage = _.find(packages, { [$package.name]: PackageNames.LegacyEA });
          const auditOnlyPackage: IPackage = _.find(packages, { [$package.name]: PackageNames.Audit });

          if (!createLegacyEAFlag && tenant.packageId !== legacyEApackage[$package._id]) {
            packageNamesToRemove.push(PackageNames.LegacyEA);
          }
          if (!auditOnlyPackageFlag && tenant.packageId !== auditOnlyPackage[$package._id]) {
            packageNamesToRemove.push(PackageNames.Audit);
          }

          return packages.filter((packageInstance: IPackage) => !packageNamesToRemove.includes(packageInstance[$package.name]));
        },
      ),
      AngularUtility.shareRef(),
    );

    this.subscription.add(
      combineLatest([
        this.form.get(FormFields.package).valueChanges.pipe(startWith(this.form.get(FormFields.package).value)),
        this.packageAddons$,
      ]).subscribe(([selectedPackage, availableAddons]: [IPackage, IPackageAddon[]]) => {
        const userWayAddon: IPackageAddon = availableAddons?.find(
          (addon: IPackageAddon) => addon[$packageAddon.name] === PackageAddonNames.UserWay_Integration,
        );
        const userWayAddonId: string = userWayAddon ? userWayAddon[$packageAddon._id] : null;
        const isUserWayPackage: boolean = this.userWayPackages?.some(
          (pkg: IPackage) => pkg[$package.name] === selectedPackage?.[$package.name],
        );
        const hasUserWayAddon: boolean = this.allSelectedAddonIds?.includes(userWayAddonId);
        this.showUserWayWebsiteLimit = Boolean(isUserWayPackage || hasUserWayAddon);
        this.showDpComplexityCalculator = selectedPackage?.[$package.settings][FunctionalAreaSetting.dpComplexityEnabled] ?? true;
      }),
    );

    this.subscription.add(
      this.tenantService
        .getPdfPagesTestState(this.tenantId)
        .pipe(take(1))
        .subscribe({
          next: (res: IScannableDocumentPdfPagesTestStateResponse): void => {
            this.allocatedResources.pdfPagesTested = res.pdfPagesTested;
            this.updateUsageLimitIfValidPackage();
          },
          error: this.onGetTenantFailure.bind(this),
        }),
    );

    this.subscription.add(
      this.tenantService
        .getDigitalPropertyLimitState(this.tenantId)
        .pipe(take(1))
        .subscribe({
          next: (res: ITenantDigitalPropertyLimitState): void => {
            this.allocatedResources.digitalProperties = res.digitalPropertyCount;
            this.updateUsageLimitIfValidPackage();
          },
          error: this.onGetTenantFailure.bind(this),
        }),
    );

    this.subscription.add(
      this.tenantService
        .getTenantUsers(this.tenantId)
        .pipe(take(1))
        .subscribe({
          next: (res: ITenantUser[]): void => {
            this.allocatedResources.academySeats = res.filter(
              (user: ITenantUser) => user[$tenantUser.integrations]?.[$tenantUserIntegrations.academy]?.enabled,
            ).length;
            this.updateUsageLimitIfValidPackage();
          },
          error: this.onGetTenantFailure.bind(this),
        }),
    );

    this.setupBreadcrumbs();
  }

  public searchSalesforceAccounts = (term$: Observable<string>): Observable<ISalesforceAccount[]> =>
    term$.pipe(
      filter(SharedCommonUtility.notNullishOrEmpty),
      withLatestFrom(this.salesforceAccounts$),
      map(([term, accounts]: [string, ISalesforceAccount[]]) => {
        const availableAccounts: ISalesforceAccount[] = [...accounts];

        return _.sortBy(availableAccounts, [$salesforceAccount.name]).filter((account: ISalesforceAccount) =>
          account[$salesforceAccount.name].toLowerCase().includes(term.toLowerCase()),
        );
      }),
    );

  public get nameInputContext(): InputFieldContext {
    return {
      label: 'tenant_name',
      field: FormFields.name,
      overrideErrors: {
        unique: this.translateService.instant('form_error_unique_name'),
      },
      disabled:
        SharedCommonUtility.notNullish(this.selectedSalesforceAccount?.[$salesforceAccount.id]) && !this.showSalesforceField,
    };
  }

  protected getOtherTenants$(): Observable<ITenant[]> {
    return this.tenantService.getTenants().pipe(
      map((response: ITenantsResponse) =>
        response[$tenants.tenants].filter((tenant: ITenant) => tenant[$tenant._id] !== this.tenantId),
      ),
      AngularUtility.shareRef(),
    );
  }

  private setupBreadcrumbs(): void {
    this.breadcrumbs = [];
  }

  public selectPackage(pack: IPackage): void {
    const originalPackage: IPackage = this.originalValuesSnapshot?.[FormFields.package];
    const currentPackage: IPackage = this.form.get(FormFields.package)?.value;
    const isSwitchingBackToOriginal: boolean =
      this.originalValuesSnapshot &&
      originalPackage?.[$package._id] === pack[$package._id] &&
      currentPackage?.[$package._id] !== pack[$package._id];

    super.selectPackage(pack);

    if (!this.preservePackageUsageLimits && isSwitchingBackToOriginal) {
      this.form.patchValue(
        {
          [FormFields.pdfPagesTestLimit]: this.originalValuesSnapshot[FormFields.pdfPagesTestLimit],
          [FormFields.monitoringPagesPerDigitalPropertyLimit]:
            this.originalValuesSnapshot[FormFields.monitoringPagesPerDigitalPropertyLimit],
          [FormFields.seatLimit]: this.originalValuesSnapshot[FormFields.seatLimit],
          [FormFields.digitalPropertyLimit]: this.originalValuesSnapshot[FormFields.digitalPropertyLimit],
        },
        { emitEvent: false },
      );

      if (this.originalValuesSnapshot[FormFields.dpComplexityCounts]) {
        const dpComplexityCountsControl = this.form.get(FormFields.dpComplexityCounts);
        if (dpComplexityCountsControl) {
          dpComplexityCountsControl.setValue(this.originalValuesSnapshot[FormFields.dpComplexityCounts], { emitEvent: false });
        }
      }
    }
  }
}
